import { mdiClockAlertOutline, mdiClockOutline } from '@mdi/js'
import { defineStore } from 'pinia'
import { ref } from 'vue'

import Network from '@/Network'
import ExceptionService from '@/Network/Services/DoctorStationException'
import { DSAttendanceStatus } from '@/constants/ds-attendance.constants'
import { FacialBiometryStatus } from '@/constants/facial-biometry.constants'
import { getApiErrorMessage } from '@/utils/apiError'
import { isProfessionalDocumentsDisabled } from '@/utils/qualificationErrors'
import { oldestWaitingFooter } from '@/modules/company/Approvals/ProfessionalDocuments/model'
import type {
  ApprovalsQueueFooter,
  ApprovalsQueueKey
} from '@/modules/company/Approvals/Hub/queues'

export interface ApprovalsQueueCount {
  /** `null` = ainda não carregado ou indisponível (ver `error`). */
  value: number | null
  isLoading: boolean
  error: string | null
}

const emptyCount = (): ApprovalsQueueCount => ({ value: null, isLoading: false, error: null })

/** Contagem; a fila cujo endpoint já traz o rodapé real devolve os dois juntos. */
type CounterWithFooter = { value: number | null; footer: ApprovalsQueueFooter | null }
type CounterResult = number | null | CounterWithFooter

const isWithFooter = (result: CounterResult): result is CounterWithFooter =>
  typeof result === 'object' && result !== null

/**
 * Contagem de "aguardando decisão" por fila, com o que os endpoints de listagem
 * já devolvem (uma request leve por fila: página de 1 item e ler o total).
 * Sem endpoint agregado no backend (pendência); rodapés do protótipo
 * ("expira hoje", "mais antigo há N dias") ficam de fora até ele existir —
 * exceto onde o próprio contador já traz o dado (Revisão de documentos).
 */
// `null` = fila sem contagem neste ambiente (card mostra "contagem indisponível", sem erro).
const COUNTERS: Record<ApprovalsQueueKey, () => Promise<CounterResult>> = {
  // Sem `statuses` o backend aplica o default IN_ADMIN_QUEUE = aguardando aprovação.
  handoverApprovals: async () =>
    (await Network.handoverInvite.listPendingApprovals({ page: 1, perPage: 1 })).meta.total,
  // Service legado ainda fora do singleton Network — mesmo uso do módulo da fila.
  exceptionality: async () => (await new ExceptionService().getStatusSummary()).awaitingApproval,
  attendance: async () =>
    (
      await Network.dsAttendance.listByCompany({
        status: [DSAttendanceStatus.PENDING_AUDIT],
        page: 1,
        limit: 1
      })
    ).count,
  // O default da fila já é PENDING_REVIEW.
  checkValidation: async () =>
    (await Network.checkValidation.listQueue({ limit: 1, offset: 0 })).total,
  facialBiometry: async () =>
    (
      await Network.facialBiometry.listDoctors({
        page: 1,
        limit: 1,
        status: FacialBiometryStatus.PENDING
      })
    ).meta.total,
  // `needsSending` do BE (NOT_SENT+REJECTED menos Antecipe pago, A1), o número
  // que a Operação persegue. `limit: 1` porque só o contador importa aqui.
  priorityPayments: async () =>
    (await Network.priorityPayments.list({ cycle: 'CURRENT', page: 1, limit: 1 })).counters
      .needsSending,
  // "Em Análise" visível para o usuário (escopo por empresa no backend, D26); o mesmo
  // summary traz o envio mais antigo, que vira o rodapé real do card.
  professionalDocuments: async () => {
    try {
      const summary = await Network.professionalDocuments.getReviewQueueSummary()
      const oldest = oldestWaitingFooter(summary.oldestWaitingSince)
      return {
        value: summary.inReview,
        footer: oldest && {
          text: oldest.text,
          icon: oldest.overdue ? mdiClockAlertOutline : mdiClockOutline,
          variant: oldest.overdue ? 'alert' : 'default'
        }
      }
    } catch (error) {
      // fallback intencional: backend com a feature desligada (404 PROFESSIONAL_DOCUMENTS_DISABLED)
      // não é erro; o card fica sem número ("contagem indisponível"), como a fila (R11).
      if (isProfessionalDocumentsDisabled(error)) return null
      throw error
    }
  }
}

// Guard de corrida: só o `fetchCounts` mais recente escreve estado.
let _fetchId = 0

export const useApprovalsHubStore = defineStore('approvalsHub', () => {
  const counts = ref<Partial<Record<ApprovalsQueueKey, ApprovalsQueueCount>>>({})
  // Rodapé vindo do backend; `null`/ausente = o card usa o texto do registro (`queue.footer`).
  const footers = ref<Partial<Record<ApprovalsQueueKey, ApprovalsQueueFooter | null>>>({})

  const countFor = (key: ApprovalsQueueKey): ApprovalsQueueCount =>
    counts.value[key] ?? emptyCount()

  const footerFor = (key: ApprovalsQueueKey): ApprovalsQueueFooter | null =>
    footers.value[key] ?? null

  /**
   * Dispara as contagens em paralelo e grava cada uma assim que chega — uma
   * fila falhando não segura nem esconde as demais. Erro vira `error` no card
   * (sem toast: o hub continua navegável).
   */
  async function fetchCounts(keys: ApprovalsQueueKey[]) {
    const fetchId = ++_fetchId
    for (const key of keys) {
      counts.value = { ...counts.value, [key]: { value: null, isLoading: true, error: null } }
    }

    await Promise.all(
      keys.map(async (key) => {
        try {
          const result = await COUNTERS[key]()
          if (fetchId === _fetchId) {
            const value = isWithFooter(result) ? result.value : result
            counts.value = { ...counts.value, [key]: { value, isLoading: false, error: null } }
            if (isWithFooter(result)) footers.value = { ...footers.value, [key]: result.footer }
          }
        } catch (error) {
          console.error(error)
          if (fetchId === _fetchId) {
            footers.value = { ...footers.value, [key]: null }
            counts.value = {
              ...counts.value,
              [key]: {
                value: null,
                isLoading: false,
                error: getApiErrorMessage(error, 'Não foi possível carregar a contagem')
              }
            }
          }
        }
      })
    )
  }

  // $reset manual — obrigatório em stores Composition API (regra do repo).
  function $reset() {
    _fetchId++
    counts.value = {}
    footers.value = {}
  }

  return { counts, footers, countFor, footerFor, fetchCounts, $reset }
})
